import React, { useState } from 'react';
import clsx from 'clsx';
import axios from 'axios';
import { KeenIcon } from "@/components";
import { enqueueSnackbar } from "notistack";

interface IDescriptionProps {
    title: string;
    description: string | null;
    className?: string;
    candidate_p_id: string;
}

const Description = ({ title, description, className, candidate_p_id }: IDescriptionProps) => {
    // Initialize state with an empty string if description is null
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editedDescription, setEditedDescription] = useState<string>(description ?? "");
    const [loading, setLoading] = useState<boolean>(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;

    // Handle Edit button click
    const handleEditClick = () => {
        setIsEditing(true);
    };

    // Handle Cancel button click
    const handleCancelClick = () => {
        setEditedDescription(description ?? ""); // Reset to original description or empty string
        setIsEditing(false);
    };

    // Handle Save button click
    const handleSaveClick = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            await axios.put(`${API_URL}/candidate/${candidate_p_id}/description`, {
                description: editedDescription,
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            setIsEditing(false);
            enqueueSnackbar("Description updated successfully.", { variant: 'success' });
            // Optionally trigger a parent update or refetch here
        } catch (err: any) {
            console.error('Error updating description:', err);
            enqueueSnackbar(`Error updating description: ${err.response?.data?.message || err.message}`, {
                variant: 'solid',
                state: 'danger'
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle input change for description editing
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setEditedDescription(e.target.value);
    };

    return (
        <div className={clsx('card', className)}>
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>

                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" />
                        Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleSaveClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="file-up" />
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={handleCancelClick}
                            disabled={loading}
                        >
                            <KeenIcon icon="tablet-delete" />
                            Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                <div className="">
                    {!isEditing ? (
                        <p className="whitespace-pre-wrap text-sm">{editedDescription || "No description available."}</p>
                    ) : (
                        <textarea
                            value={editedDescription}
                            onChange={handleInputChange}
                            placeholder="Edit description"
                            className="textarea textarea-bordered textarea-sm w-full"
                            rows={4}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export { Description, type IDescriptionProps };
